import { FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LossIcon, ProfitIcon, TagIcon } from '../../../../assets/icons';
import { WineCardListSkeleton } from '../../../../components/Skeleton';

import { TableCell, TableRow } from '../../../../components/Table';
import { getRegionColors, handleImageError } from '../../../../helpers';
import { DATA_REFS, Product } from '../../../../types/productType';
import { classNames, roundNumber, toInternalId } from '../../../../utils';
import { AppContext } from '../../../../context/ContextProvider';
import useFadeInOnScroll from '../../../hooks/useFadeInOnScroll';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';

interface DetailCardProps {
  tableRows: TableRow[];
  dateFormat: string;
  filteredItems: Product[];
  isCompact: boolean;
  onItemClick?: (row: TableRow) => void;
}

const extractCardDetails = (cells: TableCell[]) => {
  let result = {};
  for (const key in DATA_REFS) {
    const dataRef = DATA_REFS[key as keyof typeof DATA_REFS];
    if (dataRef === DATA_REFS.ASSETID) continue;
    result = {
      ...result,
      [dataRef]: (cells.find((x) => x.dataRef === dataRef) || { text: '' }).text,
    };
  }
  return result as Record<string, string>;
};

/** Build wine image URL from lwin18 code (same pattern as ProductImage) */
const getImageByLwin18 = (lwin18?: string): string => {
  if (!lwin18 || lwin18.length < 7) return '';
  return `https://cwcdst2prdctimgimprtfnct.blob.core.windows.net/height/400/${lwin18.substring(0, 7)}.png`;
};

/** Get score color based on value */
const getScoreStyle = (score: number): { bg: string; color: string } => {
  if (score >= 96) return { bg: '#16a34a', color: '#ffffff' };
  if (score >= 90) return { bg: '#22c55e', color: '#ffffff' };
  if (score >= 80) return { bg: '#eab308', color: '#ffffff' };
  return { bg: '#9ca3af', color: '#ffffff' };
};

const DetailCard: FC<DetailCardProps> = ({ tableRows, filteredItems, isCompact, onItemClick }) => {
  const { t } = useTranslation();
  const { gbpToTargetCurrency } = useContext(AppContext);
  const { isLoading, results, lastItemRef } = useInfiniteScroll(tableRows);
  const processIntersectionObserverEntry = useCallback((entry: IntersectionObserverEntry) => {
    entry.target.classList.toggle('flex', entry.isIntersecting);
    (entry.target.firstChild as HTMLElement).classList.toggle('flex', entry.isIntersecting);
    (entry.target.firstChild as HTMLElement).classList.toggle('hidden', !entry.isIntersecting);
  }, []);

  const { isItemVisible, fadeOnScrollClassName } = useFadeInOnScroll({ isLoading, processIntersectionObserverEntry });

  return (
    <div className="flex-col w-full">
      <div className="flex flex-wrap gap-4 p-3 bg-white dark:bg-slate-800 justify-center">
        {results &&
          results.length > 0 &&
          results.map((row, index) => {
            let lastElemRefOption = { ref: isItemVisible };
            if (results.length === index + 1) {
              lastElemRefOption = { ref: lastItemRef };
            }
            const data = extractCardDetails(row.cells);
            const rawData =
              (row.rowData as unknown as Product) ||
              filteredItems.find((x) => `${x.id}` === `${row.id}` || `${x.lwin18}` === `${row.id}`);

            const pl = Number(data[DATA_REFS.PROFIT_LOSS]);
            const totalCost = rawData?.totalCost || 0;
            const pnlPercentage = totalCost > 0 ? (pl / totalCost) * 100 : 0;
            const trending = pl === 0 ? 'neutral' : pl > 0 ? 'up' : 'down';

            const { color: regionColor, textColor } = getRegionColors(rawData?.cultWinesAllocationRegion);
            const itemImage = getImageByLwin18(rawData?.lwin18);
            const name = `${data[DATA_REFS.VINTAGE]} ${data[DATA_REFS.NAME]}`.trim();
            const scoreInfo = rawData?.combinedScore
              ? {
                  score: rawData.combinedScore,
                  ...getScoreStyle(rawData.combinedScore),
                }
              : null;

            return (
              <div
                id={`id-${index}`}
                {...lastElemRefOption}
                onClick={() => onItemClick && onItemClick(row)}
                key={`row-${index}`}
                className={`flex ${fadeOnScrollClassName} rounded-2xl w-full sm:w-[380px] bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 cursor-pointer overflow-hidden hover:shadow-xl hover:-translate-y-0.5 hover:border-gray-200 dark:hover:border-slate-600 transition-all duration-200 ease-out shadow-sm detail-card`}
              >
                {/* Region accent bar */}
                <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: regionColor || '#757587' }} />

                {/* Main content */}
                <div className="flex flex-col flex-1 p-4">
                  {/* Top section: Image + Wine info */}
                  <div className="flex gap-3 mb-3">
                    {/* Wine image */}
                    <div className="w-16 h-20 flex-shrink-0 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-600 rounded-lg flex items-center justify-center overflow-hidden">
                      <img
                        className="h-[72px] object-contain drop-shadow-sm"
                        alt={name}
                        src={itemImage}
                        onError={handleImageError}
                      />
                    </div>

                    {/* Wine name + region + qty */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug mb-1.5 line-clamp-2">
                        {name}
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          style={{ backgroundColor: regionColor || '#757587' }}
                          className={classNames('px-2 py-0.5 rounded-md text-xs font-semibold', textColor)}
                        >
                          {t(`common:regions.${toInternalId(rawData?.cultWinesAllocationRegion || '')}`)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {rawData?.qty}x ({rawData?.unit})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Value card */}
                  <div className="bg-gray-50 dark:bg-slate-700/60 rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                        {t('common:totalValue')}
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white">
                        {gbpToTargetCurrency.convertAndFormat(Number(data[DATA_REFS.TOTAL_VALUE]))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                        {t('common:profitLoss')}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {pl > 0 && <ProfitIcon className="w-3.5 h-3.5" />}
                        {pl < 0 && <LossIcon className="w-3.5 h-3.5" />}
                        <span
                          className={classNames(
                            'text-sm font-semibold',
                            trending === 'up'
                              ? 'text-green-600'
                              : trending === 'down'
                              ? 'text-red-500'
                              : 'text-gray-500',
                          )}
                        >
                          {pl >= 0 ? '+' : '-'}
                          {gbpToTargetCurrency.convertAndFormat(Math.abs(pl))}
                        </span>
                        <span
                          className={classNames(
                            'text-xs',
                            trending === 'up'
                              ? 'text-green-500'
                              : trending === 'down'
                              ? 'text-red-400'
                              : 'text-gray-400',
                          )}
                        >
                          ({pnlPercentage >= 0 ? '+' : ''}
                          {roundNumber(pnlPercentage, 1)}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bottom row: Score badge + For Sale tag */}
                  <div className="flex items-center justify-between mt-auto">
                    {/* Critic score badge */}
                    {scoreInfo ? (
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-7 h-7 rounded-md font-bold text-xs flex items-center justify-center shadow-sm"
                          style={{ backgroundColor: scoreInfo.bg, color: scoreInfo.color }}
                        >
                          {scoreInfo.score}
                        </div>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">Score</span>
                      </div>
                    ) : (
                      <div />
                    )}

                    {/* For Sale tag */}
                    {(rawData?.qtyForSale || 0) > 0 && (
                      <div className="bg-accent_orange rounded-full px-2.5 py-1 flex items-center text-xs">
                        <TagIcon className="mr-1.5 w-3 h-3" />
                        <span>{`${rawData?.qtyForSale}x ${t('portfolio:forSale')}`}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {isLoading && (
        <div className="p-3 bg-white dark:bg-slate-800">
          <WineCardListSkeleton count={3} />
        </div>
      )}
    </div>
  );
};

export default DetailCard;
